import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { toggleLoader } from "../../src/shared/actions/setting";

const useBlogApi = () => {
  const dispatch = useDispatch();
  const [blogs, setBlogs] = useState([]);

  // Fetch all blogs
  const fetchBlogs = () => {
    dispatch(toggleLoader(true));
    axios
      .get(process.env.REACT_APP_HOST + `/articles`)
      .then((res) => {
        setBlogs(res.data);
        dispatch(toggleLoader(false));
      })
      .catch((error) => {
        toast.error("Failed to fetch blogs.");
        dispatch(toggleLoader(false));
      });
  };

  const editBlog = (blogData) => {
    dispatch(toggleLoader(true));
    axios
      .patch(process.env.REACT_APP_HOST + `/article/edit`, blogData)
      .then((res) => {
        toast.success("Blog updated successfully!");
        fetchBlogs();
        dispatch(toggleLoader(false));
      })
      .catch((error) => {
        toast.error("Failed to update blog.");
        dispatch(toggleLoader(false));
      });
  };

  const addBlog = (blogData) => {
    dispatch(toggleLoader(true));
    axios
      .post(process.env.REACT_APP_HOST + `/article/add`, blogData)
      .then((res) => {
        toast.success("Blog added successfully!");
        fetchBlogs();
        dispatch(toggleLoader(false));
      })
      .catch((error) => {
        toast.error("Failed to add blog.");
        dispatch(toggleLoader(false));
      });
  };

  // GET /articles (fetchBlogs above) no longer carries payload.content_html — at real
  // content volume that made list responses large enough to get truncated in transit.
  // The edit page fetches a single article's body on demand via this instead.
  const fetchArticleContent = async (articleId) => {
    const { data } = await axios.get(process.env.REACT_APP_HOST + `/article/${articleId}`);
    return (data.payload && data.payload.content_html) || "";
  };

  // Uploads a blog content image: gets a short-lived presigned S3 URL from the
  // backend, PUTs the file bytes directly to S3 (no proxying through our server),
  // then returns the public URL the editor inserts into the post body.
  const uploadBlogImage = async (file) => {
    const { data } = await axios.post(
      process.env.REACT_APP_HOST + `/article/generate-image-upload-url`,
      { fileName: file.name, fileType: file.type }
    );

    // A bare axios.create() instance, not the default `axios` export — the app's
    // global request interceptor (src/App.js) attaches our own Authorization header
    // to every request on the default instance, and S3 rejects a presigned-URL
    // request that also carries an Authorization header (SignatureDoesNotMatch).
    await axios.create()(data.uploadUrl, {
      method: "put",
      data: file,
      headers: { "Content-Type": file.type },
      transformRequest: [(d) => d],
    });

    return data.publicUrl;
  };

  const deleteBlog = (id) => {
    dispatch(toggleLoader(true));
    axios
      .post(process.env.REACT_APP_HOST + `/delete`, { id })
      .then((res) => {
        toast.success("Blog deleted successfully!");
        fetchBlogs();
        dispatch(toggleLoader(false));
      })
      .catch((error) => {
        toast.error("Failed to delete blogs.");
        dispatch(toggleLoader(false));
      });
  };

  const publishBlog = (article_id) => {
    dispatch(toggleLoader(true));
    axios
      .post(process.env.REACT_APP_HOST + `/publish-article`, { article_id })
      .then((res) => {
        toast.success("Blog published successfully!");
        fetchBlogs();
        dispatch(toggleLoader(false));
      })
      .catch((error) => {
        toast.error("Failed to publish blog.");
        dispatch(toggleLoader(false));
      });
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  return {
    blogs,
    editBlog,
    addBlog,
    deleteBlog,
    publishBlog,
    uploadBlogImage,
    fetchArticleContent,
  };
};

export default useBlogApi;
